/**
 * SAP backend option — configuration & feature gating.
 *
 * Part of the isolated `sap-backend/` side project (see README.md).
 *
 * ISOLATION GUARANTEE: nothing in this folder runs unless the app is
 * explicitly started with `VITE_DATA_SOURCE=sap`. Every existing deployment
 * (mock / api / supabase) never imports or executes this code path, so the
 * live Supabase backend is completely unaffected.
 */

/**
 * Per-entity migration status. Drives both the runtime dispatch and the
 * status matrix in the README. Add an entry here the moment a SAP-side
 * implementation lands; leave everything else as the implicit "pending".
 *
 *   'sap'      — fully implemented against S/4; `sap` mode reads/writes SAP.
 *   'pending'  — not yet built; falls back to the Supabase substrate (or
 *                throws, depending on `sapFallbackEnabled`).
 *
 * Keys are the `dataService` function names so the mapping is unambiguous.
 */
export const ENTITY_STATUS: Record<string, 'sap' | 'pending'> = {
  // --- Tier 1: financial truth (target of the first build-out wave) ---
  getPeriodicActuals: 'sap', // ACDOCA via ZUI_M12_WBS_ACTUALS_O4 — proven end-to-end
  getFinancialActuals: 'sap', // ACDOCA (raw WBS×period×cost-element grain), generic odataClient
  getWbsElements: 'sap', // PRPS+PROJ+PRHI via ZUI_M12_WBS_STRUCT_O4; structure only
  getProjects: 'sap', // PROJ header via ZUI_M12_PROJECT_O4; header fields only
  // --- App-owned entities served by the CAP /ppm service (HANA Cloud) ---
  getRisks: 'sap',
  getMilestones: 'sap',
  getIssues: 'sap',
  getResourceAssignments: 'sap',
  getBaselineModels: 'sap',
  getForecastVersions: 'sap',
  getBudgetData: 'sap',
  getResourceDemand: 'sap',
  getScenarios: 'sap',
  getStrategicPriorities: 'sap',
  getResources: 'sap',
  getResourceCategories: 'sap',
  getMasterContracts: 'sap',
  getOrganizations: 'sap',
  getNotifications: 'sap',
}

/**
 * SAP-served reads that already exist but DO NOT flow through the dataService
 * seam — they have dedicated services consumed straight by their feature pages.
 * For backend-coverage purposes these are effectively "already on SAP"; they are
 * listed here for the scoreboard, not dispatched. See README status matrix.
 */
export const SAP_DIRECT_SERVICES: Record<string, string> = {
  currencyRatesService: 'ZUI_PRJ_FX_O4 / Z_M12_FX_UPSERT (read+write)',
  chartOfAccountsService: 'ZUI_M12_COA_O4 (CoA + cost-element groups, read + sync)',
  wbsActualsService: 'ZUI_M12_WBS_ACTUALS_O4 (also used directly by forecast Sync Actuals)',
}

export type DataSourceMode = 'mock' | 'api' | 'supabase' | 'sap'

/**
 * When true, an entity marked 'pending' (or missing) falls back to the
 * Supabase substrate while running in `sap` mode, so the app stays usable
 * during the incremental build-out. When false, pending entities throw
 * SapNotImplementedError — useful for measuring true SAP coverage.
 *
 * Defaults to ON so a developer can flip to `sap` mode and still navigate the
 * whole app. Set `VITE_SAP_FALLBACK=false` to make coverage gaps fail loud.
 */
export function sapFallbackEnabled(): boolean {
  try {
    return (import.meta.env.VITE_SAP_FALLBACK as string | undefined) !== 'false'
  } catch {
    return true
  }
}

/** Is a given dataService function backed by a real SAP implementation? */
export function isEntityOnSap(fn: string): boolean {
  return ENTITY_STATUS[fn] === 'sap'
}

/** Base path for the S/4 proxy chain (Vercel `/api/s4/*` → ppm-s4-proxy →
 *  BTP destination → Cloud Connector → vhcals4hcs). Identical entry point the
 *  existing read services already use. */
export const S4_PROXY_BASE = '/api/s4/odata/v4/sap/opu/odata4/sap'
